/**
 * Saisie préévaluation Manager — UI alignée auto-éval (pills, progression, état saisi).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  preevalApi,
  ApiRequestError,
  type PreevalDetailDto,
  type PreevalLineDto,
  type PreevalTvLineDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";

type ScoreEntry = { score: number | null; comment: string };
type ScoreMap = Record<string, ScoreEntry>;

const SCALE = [
  { level: 0, labelFr: "Aucun", labelEn: "None" },
  { level: 1, labelFr: "Basique", labelEn: "Basic" },
  { level: 2, labelFr: "Intermédiaire", labelEn: "Intermediate" },
  { level: 3, labelFr: "Avancé", labelEn: "Advanced" },
  { level: 4, labelFr: "Expert", labelEn: "Expert" },
];

function scoreClass(score: number | null): string {
  if (score === null || score === undefined) return "";
  return `sm-score-${score}`;
}

function groupBpml(lines: PreevalLineDto[]) {
  const mpMap = new Map<string, Map<string, PreevalLineDto[]>>();
  for (const line of lines) {
    const mp = line.macroProcess || "—";
    const bp = line.businessProcess || "—";
    if (!mpMap.has(mp)) mpMap.set(mp, new Map());
    const bpMap = mpMap.get(mp)!;
    if (!bpMap.has(bp)) bpMap.set(bp, []);
    bpMap.get(bp)!.push(line);
  }
  return Array.from(mpMap.entries()).map(([mp, bpMap]) => ({
    mp,
    bps: Array.from(bpMap.entries()).map(([bp, groupLines]) => ({ bp, lines: groupLines })),
  }));
}

function significantPart(value: string | null | undefined): string | null {
  if (value == null) return null;
  const t = value.trim();
  if (!t || t === ".") return null;
  return t;
}

function groupTransversal(lines: PreevalTvLineDto[]) {
  type SubMap = Map<string, PreevalTvLineDto[]>;
  const mpMap = new Map<string, Map<string, SubMap>>();
  for (const line of lines) {
    const mp = line.macroProcess || "—";
    const bp = line.businessProcess || "—";
    const subKey = significantPart(line.subProcess) ?? "";
    if (!mpMap.has(mp)) mpMap.set(mp, new Map());
    const bpMap = mpMap.get(mp)!;
    if (!bpMap.has(bp)) bpMap.set(bp, new Map());
    const subMap = bpMap.get(bp)!;
    if (!subMap.has(subKey)) subMap.set(subKey, []);
    subMap.get(subKey)!.push(line);
  }
  return Array.from(mpMap.entries()).map(([mp, bpMap]) => ({
    mp,
    bps: Array.from(bpMap.entries()).map(([bp, subMap]) => ({
      bp,
      subs: Array.from(subMap.entries()).map(([sub, groupLines]) => ({
        sub: sub || null,
        lines: groupLines,
      })),
    })),
  }));
}

function Chevron({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      className={`sm-assess-chevron${collapsed ? " is-collapsed" : ""}`}
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export function PreevalReviewPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const langFr = i18n.language?.toLowerCase().startsWith("fr");

  const [preeval, setPreeval] = useState<PreevalDetailDto | null>(null);
  const [scores, setScores] = useState<ScoreMap>({});
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<"all" | "pending" | "gaps">("all");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirtyEpoch, setDirtyEpoch] = useState(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scoresRef = useRef(scores);
  const preevalRef = useRef(preeval);
  const dirtyKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    scoresRef.current = scores;
  }, [scores]);
  useEffect(() => {
    preevalRef.current = preeval;
  }, [preeval]);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const res = await preevalApi.get(id);
      setPreeval(res.preeval);
      const map: ScoreMap = {};
      for (const line of res.preeval.lines) {
        map[line.bpmlLineId] = { score: line.score, comment: line.comment ?? "" };
      }
      for (const line of res.preeval.transversalLines) {
        map[`t:${line.transversalSkillId}`] = {
          score: line.score,
          comment: line.comment ?? "",
        };
      }
      dirtyKeysRef.current = new Set();
      setScores(map);
      setSaved(true);
      setDirtyEpoch(0);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
      setPreeval(null);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const readOnly = !preeval;

  const flushSave = useCallback(async (): Promise<boolean> => {
    const current = preevalRef.current;
    if (!id || !current) return true;
    const dirty = Array.from(dirtyKeysRef.current);
    if (dirty.length === 0) {
      setSaved(true);
      return true;
    }
    const map = scoresRef.current;
    const lines = dirty
      .filter((k) => !k.startsWith("t:"))
      .map((bpmlLineId) => {
        const v = map[bpmlLineId] ?? { score: null, comment: "" };
        return {
          bpmlLineId,
          score: v.score,
          comment: v.comment.trim() === "" ? null : v.comment,
        };
      });
    const transversalLines = dirty
      .filter((k) => k.startsWith("t:"))
      .map((k) => {
        const v = map[k] ?? { score: null, comment: "" };
        return {
          transversalSkillId: k.slice(2),
          score: v.score,
          comment: v.comment.trim() === "" ? null : v.comment,
        };
      });

    setBusy(true);
    setError(null);
    try {
      const res = await preevalApi.patchLines(id, { lines, transversalLines });
      setPreeval(res.preeval);
      dirtyKeysRef.current = new Set();
      setSaved(true);
      return true;
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("save_failed"));
      return false;
    } finally {
      setBusy(false);
    }
  }, [id]);

  useEffect(() => {
    if (readOnly || dirtyEpoch === 0) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void flushSave();
    }, 5000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [dirtyEpoch, readOnly, flushSave]);

  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (!saved && !readOnly) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [saved, readOnly]);

  function markDirty(key: string) {
    dirtyKeysRef.current.add(key);
    setSaved(false);
    setDirtyEpoch((n) => n + 1);
  }

  function setLineScore(key: string, level: number) {
    if (readOnly) return;
    setScores((prev) => ({
      ...prev,
      [key]: { score: level, comment: prev[key]?.comment ?? "" },
    }));
    markDirty(key);
  }

  function clearLineScore(key: string) {
    if (readOnly) return;
    setScores((prev) => ({
      ...prev,
      [key]: { score: null, comment: prev[key]?.comment ?? "" },
    }));
    markDirty(key);
  }

  function setLineComment(key: string, comment: string) {
    if (readOnly) return;
    setScores((prev) => ({
      ...prev,
      [key]: { score: prev[key]?.score ?? null, comment },
    }));
    markDirty(key);
  }

  function toggleCollapse(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const grouped = useMemo(() => (preeval ? groupBpml(preeval.lines) : []), [preeval]);

  const lineMeta = useMemo(() => {
    const map = new Map<string, { expertScore: number | null; delta: number | null }>();
    if (!preeval) return map;
    for (const l of preeval.lines) {
      map.set(l.bpmlLineId, { expertScore: l.expertScore, delta: l.delta });
    }
    for (const l of preeval.transversalLines) {
      map.set(`t:${l.transversalSkillId}`, {
        expertScore: l.expertScore,
        delta: l.delta,
      });
    }
    return map;
  }, [preeval]);

  const gapCount = useMemo(() => {
    let n = 0;
    for (const v of lineMeta.values()) {
      if (v.delta != null && v.delta !== 0) n += 1;
    }
    return n;
  }, [lineMeta]);

  if (!preeval && !error) {
    return (
      <div className="sm-page">
        <p className="sm-muted">{t("loading")}</p>
      </div>
    );
  }

  if (!preeval) {
    return (
      <div className="sm-page">
        <div className="sm-alert sm-alert-error">{error}</div>
        <Link to="/preevals" className="sm-link">
          ← {t("preevals")}
        </Link>
      </div>
    );
  }

  const totalBpml = preeval.lines.length;
  const scoredBpml = preeval.lines.filter(
    (l) => (scores[l.bpmlLineId]?.score ?? null) !== null
  ).length;
  const totalTv = preeval.transversalLines.length;
  const scoredTv = preeval.transversalLines.filter(
    (l) => (scores[`t:${l.transversalSkillId}`]?.score ?? null) !== null
  ).length;
  const total = totalBpml + totalTv;
  const scored = scoredBpml + scoredTv;
  const pending = total - scored;
  const progress = total > 0 ? Math.round((scored / total) * 100) : 0;
  const tvGrouped = groupTransversal(preeval.transversalLines);

  function CompareBadge({ lineKey }: { lineKey: string }) {
    const meta = lineMeta.get(lineKey);
    if (!meta || meta.expertScore == null) {
      return <span className="sm-preeval-compare sm-muted">{t("preeval_expert_na")}</span>;
    }
    const d = meta.delta;
    const dLabel =
      d == null ? "—" : d > 0 ? `+${d}` : String(d);
    const dClass =
      d == null || d === 0
        ? "sm-preeval-delta-zero"
        : Math.abs(d) >= 2
          ? "sm-preeval-delta-high"
          : "sm-preeval-delta-mid";
    return (
      <span className="sm-preeval-compare" title={t("preeval_compare_hint")}>
        <span className="sm-muted">{t("preeval_expert_short")}</span>{" "}
        <strong className={scoreClass(meta.expertScore)}>{meta.expertScore}</strong>
        {d != null ? (
          <span className={`sm-preeval-delta ${dClass}`}>Δ {dLabel}</span>
        ) : null}
      </span>
    );
  }

  function renderScoreControls(key: string, entry: ScoreEntry) {
    return (
      <div className="sm-assess-scores">
        {SCALE.map((s) => (
          <button
            key={s.level}
            type="button"
            title={langFr ? s.labelFr : s.labelEn}
            className={`sm-score-btn${entry.score === s.level ? ` ${scoreClass(s.level)}` : ""}`}
            onClick={() =>
              entry.score === s.level ? clearLineScore(key) : setLineScore(key, s.level)
            }
          >
            {s.level}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="sm-page">
      <PageHeader
        title={`${preeval.domain} — ${preeval.applicationLabel} — ${preeval.year}`}
        subtitle={`${t("preeval_for")} ${preeval.expertName} (${preeval.expertInternalId})`}
        actions={
          <div className="sm-form-actions">
            <Link
              to={`/preevals/gaps?year=${preeval.year}&domain=${encodeURIComponent(preeval.domain)}&applicationId=${preeval.applicationId}&expertUserId=${preeval.expertUserId}`}
              className="sm-btn sm-btn-secondary"
            >
              {t("preeval_compare")}
            </Link>
            <Link to="/preevals" className="sm-btn sm-btn-ghost">
              {t("back_to_list")}
            </Link>
            <button
              type="button"
              className="sm-btn sm-btn-primary"
              disabled={busy}
              onClick={() => void flushSave()}
            >
              {busy ? "…" : t("save")}
              {saved ? " ✓" : ""}
            </button>
          </div>
        }
      />

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      {preeval.assessmentStatus ? (
        <div className="sm-alert sm-alert-info" style={{ marginBottom: 12 }}>
          {t("preeval_compare_ready", { status: preeval.assessmentStatus })}{" "}
          <Link
            to={`/preevals/gaps?year=${preeval.year}&domain=${encodeURIComponent(preeval.domain)}&applicationId=${preeval.applicationId}&expertUserId=${preeval.expertUserId}`}
            className="sm-link"
          >
            {t("preeval_compare")} →
          </Link>
          {preeval.assessmentId ? (
            <>
              {" · "}
              <Link to={`/assessments/${preeval.assessmentId}`} className="sm-link">
                {t("preeval_open_assessment")}
              </Link>
            </>
          ) : null}
        </div>
      ) : (
        <div className="sm-alert sm-alert-info" style={{ marginBottom: 12, background: "#fffbeb", borderColor: "#fde68a", color: "#92400e" }}>
          {t("preeval_compare_waiting")}
        </div>
      )}

      <div className="sm-preeval-status">
        <div className="sm-assess-progress" style={{ marginBottom: 0, flex: 1 }}>
          <div className="sm-progress" aria-hidden>
            <span style={{ width: `${progress}%` }} />
          </div>
          <span className="sm-muted" style={{ whiteSpace: "nowrap" }}>
            <strong>
              {scored}/{total}
            </strong>{" "}
            ({progress}%)
            <span className="sm-muted">
              {" "}
              · BPML {scoredBpml}/{totalBpml}
              {totalTv > 0 ? ` · TV ${scoredTv}/${totalTv}` : ""}
            </span>
            {pending > 0 ? (
              <span className="sm-preeval-pending-count">
                {" "}
                · {pending} {t("preeval_pending")}
              </span>
            ) : (
              <span className="sm-preeval-complete-hint"> · {t("preeval_complete")}</span>
            )}
          </span>
        </div>
        <div className="sm-form-actions" style={{ flexShrink: 0 }}>
          <button
            type="button"
            className={`sm-btn sm-btn-sm${filter === "all" ? " sm-btn-secondary" : " sm-btn-ghost"}`}
            onClick={() => setFilter("all")}
          >
            {t("all")}
          </button>
          <button
            type="button"
            className={`sm-btn sm-btn-sm${filter === "pending" ? " sm-btn-secondary" : " sm-btn-ghost"}`}
            onClick={() => setFilter("pending")}
          >
            {t("preeval_filter_pending")} ({pending})
          </button>
          {preeval.assessmentStatus ? (
            <button
              type="button"
              className={`sm-btn sm-btn-sm${filter === "gaps" ? " sm-btn-secondary" : " sm-btn-ghost"}`}
              onClick={() => setFilter("gaps")}
            >
              {t("preeval_filter_gaps")} ({gapCount})
            </button>
          ) : null}
        </div>
      </div>

      <p className="sm-muted" style={{ margin: "8px 0 12px", fontSize: 13 }}>
        {t("preeval_privacy_hint")} · {t("preeval_score_hint")}
      </p>

      <div className="sm-score-legend sm-score-legend-rich" aria-label={t("preeval_scale_legend")}>
        {SCALE.map((s) => (
          <span key={s.level} className="sm-score-legend-item">
            <span className={`sm-score-legend-swatch ${scoreClass(s.level)}`}>{s.level}</span>
            {langFr ? s.labelFr : s.labelEn}
          </span>
        ))}
      </div>

      <div className="sm-assess-stack">
        {grouped.map(({ mp, bps }) => {
          const mpLines = bps.flatMap((b) => b.lines);
          const mpScored = mpLines.filter(
            (l) => (scores[l.bpmlLineId]?.score ?? null) !== null
          ).length;
          const visibleBps = bps
            .map(({ bp, lines }) => ({
              bp,
              lines:
                filter === "pending"
                  ? lines.filter((l) => (scores[l.bpmlLineId]?.score ?? null) === null)
                  : filter === "gaps"
                    ? lines.filter((l) => {
                        const d = lineMeta.get(l.bpmlLineId)?.delta;
                        return d != null && d !== 0;
                      })
                    : lines,
            }))
            .filter((b) => b.lines.length > 0);
          if (filter === "pending" && visibleBps.length === 0) return null;
          const mpKey = mp;
          return (
            <div key={mp} className="sm-card sm-assess-block">
              <button
                type="button"
                className="sm-assess-macro"
                onClick={() => toggleCollapse(mpKey)}
              >
                <span>
                  {mp}{" "}
                  <span className="sm-muted" style={{ fontWeight: 500, fontSize: 12 }}>
                    ({mpScored}/{mpLines.length})
                  </span>
                </span>
                <Chevron collapsed={collapsed.has(mpKey)} />
              </button>
              {!collapsed.has(mpKey)
                ? visibleBps.map(({ bp, lines }) => (
                    <div key={bp}>
                      <div className="sm-assess-bp">{bp}</div>
                      {lines.map((line) => {
                        const key = line.bpmlLineId;
                        const entry = scores[key] ?? { score: null, comment: "" };
                        const isScored = entry.score !== null;
                        const sub = significantPart(line.subProcess);
                        const task = significantPart(line.task);
                        const label = significantPart(line.label);
                        return (
                          <div
                            key={key}
                            className={`sm-assess-line${isScored ? " is-scored" : " is-pending"}`}
                          >
                            <div className="sm-assess-line-label">
                              <div>
                                {sub ? (
                                  <>
                                    {sub}
                                    {task ? <span className="sm-assess-task">{task}</span> : null}
                                  </>
                                ) : task ? (
                                  <em>{task}</em>
                                ) : label ? (
                                  label
                                ) : (
                                  <em className="sm-muted">{t("bp_level_placeholder")}</em>
                                )}
                                {line.isCritical ? (
                                  <span
                                    className="sm-badge-pill sm-badge-red"
                                    style={{ marginLeft: 6 }}
                                  >
                                    {t("critical")}
                                  </span>
                                ) : null}
                              </div>
                              <span className="sm-assess-meta">
                                {isScored
                                  ? t("preeval_line_scored", { score: entry.score })
                                  : t("preeval_line_pending")}
                              </span>
                            </div>
                            {renderScoreControls(key, entry)}
                            <CompareBadge lineKey={key} />
                            <input
                              className="sm-input sm-assess-comment"
                              placeholder={t("add_comment")}
                              value={entry.comment}
                              onChange={(e) => setLineComment(key, e.target.value)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))
                : null}
            </div>
          );
        })}
      </div>

      {totalTv > 0 ? (
        <div className="sm-assess-stack" style={{ marginTop: 24 }}>
          <h2 className="sm-assess-tv-title">
            {t("transversal_skills")} ({scoredTv}/{totalTv})
          </h2>
          <p className="sm-alert sm-alert-info">{t("preeval_tv_shared_hint")}</p>
          {tvGrouped.map(({ mp, bps }) => {
            const key = `t:${mp}`;
            const mpLines = bps.flatMap((b) => b.subs.flatMap((s) => s.lines));
            const visibleBps = bps
              .map(({ bp, subs }) => ({
                bp,
                subs: subs
                  .map(({ sub, lines }) => ({
                    sub,
                    lines:
                      filter === "pending"
                        ? lines.filter(
                            (l) =>
                              (scores[`t:${l.transversalSkillId}`]?.score ?? null) === null
                          )
                        : filter === "gaps"
                          ? lines.filter((l) => {
                              const d = lineMeta.get(`t:${l.transversalSkillId}`)?.delta;
                              return d != null && d !== 0;
                            })
                          : lines,
                  }))
                  .filter((s) => s.lines.length > 0),
              }))
              .filter((b) => b.subs.length > 0);
            if (filter === "pending" && visibleBps.length === 0) return null;
            return (
              <div key={key} className="sm-card sm-assess-block">
                <button
                  type="button"
                  className="sm-assess-macro sm-assess-macro-tv"
                  onClick={() => toggleCollapse(key)}
                >
                  <span>
                    {mp}{" "}
                    <span style={{ fontWeight: 500, fontSize: 12, opacity: 0.85 }}>
                      (
                      {
                        mpLines.filter(
                          (l) =>
                            (scores[`t:${l.transversalSkillId}`]?.score ?? null) !== null
                        ).length
                      }
                      /{mpLines.length})
                    </span>
                  </span>
                  <Chevron collapsed={collapsed.has(key)} />
                </button>
                {!collapsed.has(key)
                  ? visibleBps.map(({ bp, subs }) => (
                      <div key={bp}>
                        <div className="sm-assess-bp">{bp}</div>
                        {subs.map(({ sub, lines }) => (
                          <div key={`${bp}|${sub ?? ""}`}>
                            {sub ? <div className="sm-assess-sub-tv">{sub}</div> : null}
                            {lines.map((line) => {
                              const sk = `t:${line.transversalSkillId}`;
                              const entry = scores[sk] ?? { score: null, comment: "" };
                              const isScored = entry.score !== null;
                              const task = significantPart(line.task);
                              return (
                                <div
                                  key={sk}
                                  className={`sm-assess-line${isScored ? " is-scored" : " is-pending"}`}
                                >
                                  <div className="sm-assess-line-label">
                                    <div>
                                      {task ? (
                                        task
                                      ) : sub ? (
                                        <em>{sub}</em>
                                      ) : (
                                        bp
                                      )}
                                    </div>
                                    <span className="sm-assess-meta">
                                      {isScored
                                        ? t("preeval_line_scored", { score: entry.score })
                                        : t("preeval_line_pending")}
                                    </span>
                                  </div>
                                  {renderScoreControls(sk, entry)}
                                  <CompareBadge lineKey={sk} />
                                  <input
                                    className="sm-input sm-assess-comment"
                                    placeholder={t("add_comment")}
                                    value={entry.comment}
                                    onChange={(e) => setLineComment(sk, e.target.value)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ))
                  : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
